import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { subscribeAllThreads, subscribeThreadMessages, sendMessage, markThreadRead } from '../../utils/messaging';

export default function AdminMessagesPanel({ initialStudentId = '' }) {
  const { currentUser } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(initialStudentId || '');
  const [studentName, setStudentName] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [lookup, setLookup] = useState('');
  const [lookupMsg, setLookupMsg] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => subscribeAllThreads(setThreads), []);

  useEffect(() => {
    if (initialStudentId) setSelectedStudentId(initialStudentId);
  }, [initialStudentId]);

  useEffect(() => {
    if (!selectedStudentId) {
      setMessages([]);
      return undefined;
    }
    markThreadRead(selectedStudentId, 'admin').catch(() => {});
    return subscribeThreadMessages(selectedStudentId, setMessages);
  }, [selectedStudentId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function lookupStudent() {
    setLookupMsg('');
    const qText = lookup.trim().toLowerCase();
    if (!qText) return;
    try {
      let docs = [];
      const emailSnap = await getDocs(
        query(collection(db, 'users'), where('emailLower', '==', qText), limit(5))
      );
      docs = emailSnap.docs;
      if (!docs.length) {
        const regSnap = await getDocs(
          query(collection(db, 'users'), where('registrationNumber', '==', lookup.trim().toUpperCase()), limit(5))
        );
        docs = regSnap.docs;
      }
      if (!docs.length) {
        setLookupMsg('No student found.');
        return;
      }
      const u = docs[0].data();
      setSelectedStudentId(docs[0].id);
      setStudentName(u.displayName || u.email);
      setLookupMsg(`Found: ${u.displayName || u.email}`);
    } catch (err) {
      setLookupMsg(err?.message || 'Lookup failed.');
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!selectedStudentId || !draft.trim()) return;
    setSending(true);
    try {
      await sendMessage({
        fromUid: currentUser?.uid,
        fromRole: 'admin',
        fromName: currentUser?.displayName || 'Administration',
        studentUserId: selectedStudentId,
        text: draft,
      });
      setDraft('');
    } catch (err) {
      alert(err?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  function formatTime(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h2 style={s.title}>Student messages</h2>
        <p style={s.sub}>Two-way messaging between administration and students through the portal.</p>
      </div>

      <div style={s.layout}>
        <aside style={s.sidebar}>
          <div style={s.lookupRow}>
            <input
              style={s.input}
              placeholder="Email or reg no…"
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
            />
            <button type="button" onClick={lookupStudent} style={s.lookupBtn}>
              Find
            </button>
          </div>
          {lookupMsg ? <small style={{ color: '#64748b', padding: '0 12px 8px', display: 'block' }}>{lookupMsg}</small> : null}

          {threads.length === 0 ? (
            <p style={s.emptySide}>No conversations yet</p>
          ) : (
            threads.map((t) => (
              <button
                key={t.id}
                type="button"
                style={s.threadBtn(selectedStudentId === t.studentUserId)}
                onClick={() => {
                  setSelectedStudentId(t.studentUserId);
                  setStudentName(t.studentName || '');
                }}
              >
                <span style={s.threadId}>{t.studentName || (t.studentUserId ? `${t.studentUserId.slice(0, 8)}…` : 'Student')}</span>
                <span style={s.threadPreview}>{t.lastMessage}</span>
              </button>
            ))
          )}
        </aside>

        <main style={s.chat}>
          {!selectedStudentId ? (
            <div style={s.emptyChat}>Select or find a student to start messaging</div>
          ) : (
            <>
              <div style={s.chatHead}>
                <strong>
                  {studentName
                    || threads.find((t) => t.studentUserId === selectedStudentId)?.studentName
                    || `Student ${selectedStudentId.slice(0, 8)}…`}
                </strong>
              </div>
              <div style={s.messages}>
                {messages.map((m) => (
                  <div key={m.id} style={s.bubble(m.fromRole === 'admin')}>
                    <span style={s.bubbleMeta}>{m.fromName || m.fromRole} · {formatTime(m.createdAt)}</span>
                    <p style={s.bubbleText}>{m.text}</p>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={handleSend} style={s.compose}>
                <input
                  style={s.composeInput}
                  placeholder="Type a message to the student…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <button type="submit" disabled={sending || !draft.trim()} style={s.sendBtn(sending)}>
                  Send
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { marginBottom: 20 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#4b00d1', marginBottom: 6 },
  sub: { fontSize: 13, color: '#64748b', margin: 0 },
  layout: { display: 'flex', gap: 16, minHeight: 480, border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', background: '#fff' },
  sidebar: { width: 260, borderRight: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', flexDirection: 'column' },
  lookupRow: { display: 'flex', gap: 6, padding: 12 },
  input: { flex: 1, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13 },
  lookupBtn: { padding: '8px 12px', borderRadius: 8, border: 'none', background: '#4b00d1', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  emptySide: { padding: 20, color: '#94a3b8', fontSize: 13, textAlign: 'center' },
  threadBtn: (active) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '12px 14px',
    border: 'none',
    borderBottom: '1px solid #e2e8f0',
    background: active ? '#eef2ff' : 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
  }),
  threadId: { fontSize: 11, fontWeight: 700, color: '#64748b', fontFamily: 'monospace' },
  threadPreview: { fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chat: { flex: 1, display: 'flex', flexDirection: 'column' },
  emptyChat: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 },
  chatHead: { padding: '14px 18px', borderBottom: '1px solid #e2e8f0', fontSize: 14 },
  messages: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  bubble: (isAdmin) => ({
    alignSelf: isAdmin ? 'flex-end' : 'flex-start',
    maxWidth: '75%',
    background: isAdmin ? '#4b00d1' : '#f1f5f9',
    color: isAdmin ? '#fff' : '#1e293b',
    borderRadius: 12,
    padding: '10px 14px',
  }),
  bubbleMeta: { fontSize: 10, opacity: 0.75, display: 'block', marginBottom: 4 },
  bubbleText: { margin: 0, fontSize: 13, lineHeight: 1.5 },
  compose: { display: 'flex', gap: 8, padding: 14, borderTop: '1px solid #e2e8f0' },
  composeInput: { flex: 1, padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14 },
  sendBtn: (disabled) => ({
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#94a3b8' : '#059669',
    color: '#fff',
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
